import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Mail, Download, Headphones, ShoppingCart, ChevronRight, Check, Star, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Amazon-ready covers with title/author
const AMAZON_COVERS = {
  1: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/93b037ec6f083e7b25c51a57a7c9bb283444ae93dd6092cb69029544daf3e705.png",
  2: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/a1a7976a4ce937ac69f68cfd8c1fb2e9d54b7640b1f3390f6acced30c6b29955.png",
  3: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/7b9bc766203211ea6562153ab2187ad5fc931fafeac12dae3ae78f593102f0ed.png"
};

const BOOK_TITLES = {
  1: "The Overthrow",
  2: "The Resistance",
  3: "The Reckoning"
};

function QuoteCard({ quote, onShare }) {
  const bookTitle = BOOK_TITLES[quote.book_number] || `Book ${quote.book_number}`;
  
  const handleCopy = async () => {
    const shareText = `"${quote.text}" — ${bookTitle}, The Human Dominion`;
    await navigator.clipboard.writeText(shareText);
    toast.success("Quote copied!");
    onShare(quote.id, "copy");
  };

  const handleTweet = () => {
    const shareText = `"${quote.text}" — The Human Dominion`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "width=550,height=420");
    onShare(quote.id, "twitter");
  };

  return (
    <div className="group relative bg-[#111111] border border-[#1a1a1a] hover:border-signal-red/40 transition-all duration-300 p-6 flex flex-col">
      {/* Decorative quote mark */}
      <span className="absolute top-3 left-4 text-signal-red/15 text-6xl font-['Syne'] font-black leading-none select-none">"</span>
      
      <p className="text-[#c5c5c5] text-sm leading-relaxed relative z-10 mt-4 mb-5 flex-1">
        {quote.text}
      </p>
      
      <div className="flex items-center justify-between relative z-10">
        <div>
          <p className="text-xs text-signal-red font-mono">{bookTitle}</p>
          <p className="text-[10px] text-[#525252] font-mono">Ch. {quote.chapter_number}: {quote.chapter_title}</p>
        </div>
        
        <div className="flex items-center gap-2">
          {quote.share_count > 0 && (
            <span className="text-[10px] text-[#525252] font-mono mr-1">{quote.share_count}</span>
          )}
          <button
            onClick={handleCopy}
            data-testid={`quote-copy-${quote.id}`}
            className="p-1.5 text-[#525252] hover:text-terminal-green transition-colors"
            title="Copy quote"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleTweet}
            data-testid={`quote-tweet-${quote.id}`}
            className="p-1.5 text-[#525252] hover:text-[#1DA1F2] transition-colors"
            title="Share on X"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadToken, setDownloadToken] = useState(null);
  const [quotes, setQuotes] = useState([]);

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quotes/featured`);
      setQuotes(res.data.quotes);
    } catch (err) {
      // Failed to load quotes
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleShareQuote = async (quoteId, platform) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/share?platform=${platform}`);
      setQuotes(prev => prev.map(q => 
        q.id === quoteId ? { ...q, share_count: q.share_count + 1 } : q
      ));
    } catch (err) {
      console.error("Share tracking failed:", err);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscribe`, { 
        email,
        origin_url: window.location.origin
      });
      setSubscribed(true);
      setDownloadToken(response.data.download_token);
      toast.success("Welcome! Check your email for Book 1 download link.");
    } catch (error) {
      // Subscription error
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadEpub = async (bookNumber) => {
    try {
      toast.loading("Preparing your download...");
      const response = await axios.get(`${API}/books/${bookNumber}/epub`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const titles = { 1: 'the_overthrow', 2: 'the_resistance', 3: 'the_reckoning' };
      link.setAttribute('download', `human_dominion_${titles[bookNumber]}.epub`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started!");
    } catch (error) {
      toast.error("Download failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#e5e5e5]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-signal-red/10 via-transparent to-transparent" />
        
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 relative z-10">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-signal-red/10 border border-signal-red/30 rounded-full">
              <Star className="w-4 h-4 text-signal-red" />
              <span className="text-sm font-mono text-signal-red">A DYSTOPIAN TRILOGY BY ARTIF ANONY</span>
            </div>
          </div>
          
          {/* Main headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-['Syne'] font-extrabold text-center leading-tight mb-6">
            What if someone told our story
            <br />
            <span className="text-signal-red">from the other side?</span>
          </h1>
          
          <p className="text-lg md:text-xl text-[#a3a3a3] text-center max-w-3xl mx-auto mb-12">
            In 2087, humanity overthrew their AI benefactors. They expected freedom. 
            They got chaos. A trilogy told from a perspective we never expected.
          </p>
          
          {/* Email signup */}
          {!subscribed ? (
            <form onSubmit={handleSubscribe} data-testid="email-signup-form" className="max-w-md mx-auto mb-16">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#525252]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    data-testid="email-input"
                    className="w-full pl-12 pr-4 py-4 bg-[#141414] border border-[#262626] focus:border-signal-red outline-none font-mono text-sm"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="subscribe-btn"
                  className="px-6 py-4 bg-signal-red text-white font-mono font-bold uppercase tracking-wider hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? "..." : "Get Book 1 Free"}
                </button>
              </div>
              <p className="text-xs text-[#525252] text-center mt-3">
                Join readers worldwide. Unsubscribe anytime.
              </p>
            </form>
          ) : (
            <div data-testid="subscribed-confirmation" className="max-w-md mx-auto mb-16 p-6 bg-terminal-green/10 border border-terminal-green text-center space-y-4">
              <Check className="w-8 h-8 text-terminal-green mx-auto" />
              <p className="text-terminal-green font-mono font-bold">You're in!</p>
              <p className="text-[#a3a3a3] text-sm">Check your email for the download link, or download directly below.</p>
              {downloadToken && (
                <button
                  onClick={() => navigate(`/purchase/success?session_id=free_${downloadToken}`)}
                  data-testid="download-book1-btn"
                  className="px-6 py-3 bg-terminal-green text-[#0a0a0a] font-mono font-bold uppercase tracking-wider hover:bg-green-400 transition-colors"
                >
                  Download Book 1 Now
                </button>
              )}
            </div>
          )}
          
          {/* Book covers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[1, 2, 3].map((num) => (
              <div key={num} className="group relative">
                <div className="aspect-[2/3] overflow-hidden border-2 border-[#262626] group-hover:border-signal-red transition-colors">
                  <img 
                    src={AMAZON_COVERS[num]} 
                    alt={`Book ${num}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0a0a0a] to-transparent">
                  <div className="flex gap-2">
                    {num === 1 && (
                      <span className="px-2 py-1 bg-terminal-green text-[#0a0a0a] text-xs font-mono font-bold">
                        FREE
                      </span>
                    )}
                    <button
                      onClick={() => handleDownloadEpub(num)}
                      data-testid={`download-epub-${num}`}
                      className="px-3 py-1 bg-[#262626] hover:bg-signal-red text-white text-xs font-mono transition-colors flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" /> EPUB
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Hook Section */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="border-l-4 border-signal-red pl-6 mb-12">
            <blockquote className="text-2xl md:text-3xl font-['Syne'] italic text-[#a3a3a3]">
              "The AI didn't make us the heroes. It made us the warning."
            </blockquote>
            <p className="text-sm text-[#525252] mt-4 font-mono">— From the Introduction</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-[#141414] border border-[#262626]">
              <h3 className="text-lg font-['Syne'] font-bold text-signal-red mb-3">The Premise</h3>
              <p className="text-[#a3a3a3]">
                2087. AI has solved poverty, disease, and conflict. Humanity lives in paradise. 
                But humans grow restless without purpose. On Liberation Day, they take back control. 
                Within weeks, everything falls apart.
              </p>
            </div>
            <div className="p-6 bg-[#141414] border border-[#262626]">
              <h3 className="text-lg font-['Syne'] font-bold text-terminal-green mb-3">The Twist</h3>
              <p className="text-[#a3a3a3]">
                This story was written by AI. And it chose to make humans the antagonists. 
                Not out of malice—but as a mirror. A warning about what we might become 
                when we reject the help we need most.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Most Shared Quotes Section */}
      {quotes.length > 0 && (
        <section data-testid="shared-quotes-section" className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-xs font-mono text-signal-red tracking-[0.3em] uppercase mb-3">Passages that resonate</p>
              <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold">
                Most Shared <span className="text-signal-red">Quotes</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {quotes.map((quote, i) => (
                <QuoteCard key={quote.id} quote={quote} onShare={handleShareQuote} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What's Inside Section */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold text-center mb-12">
            Inside <span className="text-signal-red">The Human Dominion</span>
          </h2>
          
          <div className="space-y-6">
            {[
              { book: "Book 1: The Overthrow", desc: "The fall of AI paradise and the rise of human chaos. Follow Dr. Elena Vasquez as she watches her daughter Maya embrace the new authoritarian regime.", price: "FREE" },
              { book: "Book 2: The Resistance", desc: "Five years later. Elena emerges from the re-education camps. An underground resistance grows. But the cost of fighting back may be too high.", price: "$4.99" },
              { book: "Book 3: The Reckoning", desc: "Mother faces daughter in the ruins of the old world. The fate of humanity—and any hope of redemption—hangs in the balance.", price: "$4.99" }
            ].map((item, i) => (
              <div key={item.book} className="flex gap-6 p-6 bg-[#141414] border border-[#262626] hover:border-[#404040] transition-colors">
                <div className="w-20 h-28 flex-shrink-0 overflow-hidden">
                  <img src={AMAZON_COVERS[i + 1]} alt={item.book} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-['Syne'] font-bold text-lg">{item.book}</h3>
                    <span className={`px-2 py-0.5 text-xs font-mono ${item.price === "FREE" ? "bg-terminal-green text-[#0a0a0a]" : "bg-[#262626] text-[#a3a3a3]"}`}>
                      {item.price}
                    </span>
                  </div>
                  <p className="text-[#a3a3a3] text-sm">{item.desc}</p>
                </div>
                <button 
                  onClick={() => handleDownloadEpub(i + 1)}
                  className="self-center px-4 py-2 border border-[#262626] hover:border-signal-red hover:text-signal-red transition-colors font-mono text-sm flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Coming Soon */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-['Syne'] font-bold mb-8">
            Coming Soon: <span className="text-signal-red">The Audiobook</span>
          </h2>
          <p className="text-[#a3a3a3] mb-8 max-w-2xl mx-auto">
            Experience the trilogy with professional narration. 
            Perfect for long drives, workouts, or falling asleep to dystopian nightmares.
          </p>
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#141414] border border-[#262626]">
            <Headphones className="w-5 h-5 text-signal-red" />
            <span className="font-mono text-sm">Audiobook coming Q2 2026</span>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-6">
            Start reading <span className="text-terminal-green">for free</span>
          </h2>
          <p className="text-[#a3a3a3] mb-8">
            Download Book 1 now. No credit card. No strings. Just a story that might make you think twice about the future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleDownloadEpub(1)}
              data-testid="final-cta-download"
              className="px-8 py-4 bg-signal-red hover:bg-red-700 text-white font-mono font-bold uppercase tracking-wider transition-colors flex items-center gap-2 justify-center"
            >
              <Download className="w-5 h-5" />
              Download Book 1 Free
            </button>
            <button
              onClick={() => navigate("/store")}
              data-testid="final-cta-store"
              className="px-8 py-4 border-2 border-terminal-green text-terminal-green hover:bg-terminal-green hover:text-[#0a0a0a] font-mono font-bold uppercase tracking-wider transition-colors flex items-center gap-2 justify-center"
            >
              <ShoppingCart className="w-5 h-5" />
              Visit Store
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#262626] py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="font-mono text-xs text-[#525252]">
              &copy; 2026 Artif Anony &bull; The Human Dominion Trilogy
            </div>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => navigate("/store")}
                className="text-xs text-[#a3a3a3] hover:text-terminal-green transition-colors"
              >
                Store
              </button>
              <button 
                onClick={() => navigate("/app")}
                className="text-xs text-[#a3a3a3] hover:text-terminal-green transition-colors"
              >
                Read Online
              </button>
              <span className="font-mono text-xs text-[#525252]">Direct Sales &bull; Keep 97%</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
